import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  type MessagePayload,
  type Messaging,
} from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
};

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

export function initializeFirebase(): FirebaseApp | null {
  if (app) {
    return app;
  }

  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn("Firebase not configured. Push notifications will not work.");
    return null;
  }

  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
    return app;
  }

  try {
    app = initializeApp(firebaseConfig);
    return app;
  } catch (error) {
    console.error("Failed to initialize Firebase:", error);
    return null;
  }
}

function sendFirebaseConfigToServiceWorker(registration: ServiceWorkerRegistration) {
  const worker = registration.active || registration.waiting || registration.installing;
  worker?.postMessage({
    type: "FIREBASE_CONFIG",
    config: firebaseConfig,
  });
}

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> | null {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  if (swRegistrationPromise) {
    return swRegistrationPromise;
  }

  swRegistrationPromise = navigator.serviceWorker
    .register("/firebase-messaging-sw.js", { scope: "/" })
    .then((registration) => {
      sendFirebaseConfigToServiceWorker(registration);
      return registration;
    })
    .catch((error) => {
      console.warn("Service Worker registration skipped:", error);
      swRegistrationPromise = null;
      return null;
    });

  return swRegistrationPromise;
}

export async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!swRegistrationPromise) {
    const promise = registerServiceWorker();
    if (!promise) {
      return null;
    }
  }

  try {
    return await swRegistrationPromise!;
  } catch {
    return null;
  }
}

export function getFirebaseMessaging(): Messaging | null {
  if (messaging) {
    return messaging;
  }

  if (typeof window === "undefined") {
    return null;
  }

  const firebaseApp = initializeFirebase();
  if (!firebaseApp) {
    return null;
  }

  try {
    messaging = getMessaging(firebaseApp);
    return messaging;
  } catch (error) {
    console.error("Failed to get Firebase Messaging:", error);
    return null;
  }
}

export async function getFCMToken(): Promise<string | null> {
  const messagingInstance = getFirebaseMessaging();
  if (!messagingInstance) {
    return null;
  }

  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY || "";
  if (!vapidKey) {
    return null;
  }

  try {
    const swRegistration = await getServiceWorkerRegistration();
    if (!swRegistration) {
      return null;
    }

    const token = await getToken(messagingInstance, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    return token || null;
  } catch (error) {
    console.error("Error getting FCM token:", error);
    return null;
  }
}

export function onForegroundMessage(
  callback: (payload: MessagePayload) => void,
): (() => void) | null {
  const messagingInstance = getFirebaseMessaging();
  if (!messagingInstance) {
    return null;
  }

  try {
    return onMessage(messagingInstance, callback);
  } catch (error) {
    console.error("Error setting up foreground message handler:", error);
    return null;
  }
}
